import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { quoteDetailPageQueryOptions, type QuoteDetailPageSearch } from "~/app/api/pageData/quoteDetailPageQuery";
import type { QuoteDetailPageInitialData } from "~/app/api/pageData/quoteDetailPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  createQuoteDetailsPage,
  type QuoteDetailsPageProps,
} from "~/features/orders/quoteDetails/components/v2/QuoteDetailsPage";

const QUOTE_DETAIL_PATH = "/quotes/$id";

function QuoteDetailRoute() {
  const result = useLoaderData({
    from: QUOTE_DETAIL_PATH,
  }) as PageInitialData<QuoteDetailPageInitialData>;
  const shellProps = useStorefrontShellProps();
  const { id } = quoteDetailRoute.useParams();
  const pageProps = parsePageData(result.data?.pageDataJson);

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {pageProps ? createQuoteDetailsPage(pageProps, id) : null}
    </StorefrontShellFrame>
  );
}

function parsePageData(pageDataJson: string | undefined): QuoteDetailsPageProps | null {
  if (!pageDataJson) {
    return null;
  }
  try {
    return JSON.parse(pageDataJson) as QuoteDetailsPageProps;
  } catch {
    return null;
  }
}

export function parseQuoteDetailSearch(search: Record<string, unknown>): QuoteDetailPageSearch {
  return {
    account: typeof search.account === "string" ? search.account : undefined,
    salesOrg: typeof search.salesOrg === "string" ? search.salesOrg : undefined,
  };
}

export async function loadQuoteDetailPageData(
  context: { queryClient: QueryClient },
  id: string,
  search: QuoteDetailPageSearch = {},
) {
  const result = await context.queryClient.ensureQueryData(quoteDetailPageQueryOptions({ ...search, id }));

  return unwrapPageInitialData(result);
}

export const quoteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: QUOTE_DETAIL_PATH,
  validateSearch: parseQuoteDetailSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params, deps }) => loadQuoteDetailPageData(context, params.id, deps),
  component: QuoteDetailRoute,
});
